import type { WorkbenchPropagationRowAssignment } from './workbenchPropagation'
import type { AnchoredRow } from '../types/anchoredRowTypes'

export type LedgerSafeTranspositionDisplayRow = {
  key: string
  row: AnchoredRow
  kind: 'canonical' | 'projection'
  sourceRowId: string
  insertAfterRowId: string | null
  segmentIndex: number | null
}

export type LedgerSafeTranspositionDisplayModel = {
  displayRows: LedgerSafeTranspositionDisplayRow[]
  projectedSourceRowIds: Set<string>
}

type LedgerSafeProjectedDisplayRow = LedgerSafeTranspositionDisplayRow & {
  kind: 'projection'
  segmentIndex: number
}

export const buildLedgerSafeTranspositionDisplayModel = ({
  rows,
  rowAssignments,
}: {
  rows: AnchoredRow[]
  rowAssignments: WorkbenchPropagationRowAssignment[]
}): LedgerSafeTranspositionDisplayModel => {
  const segmentIndexByRowId = new Map<string, number>()
  const canonicalIndexByRowId = new Map<string, number>()
  const translationStatusByRowId = new Map<string, string | undefined>()
  rows.forEach((row, index) => {
    canonicalIndexByRowId.set(row.id, index)
  })
  rowAssignments.forEach(assignment => {
    if (typeof assignment.segmentIndex === 'number') {
      segmentIndexByRowId.set(assignment.rowId, assignment.segmentIndex)
    }
    translationStatusByRowId.set(assignment.rowId, assignment.translationStatus)
  })

  const projectedSourceRowIds = new Set<string>()
  rows.forEach((row, canonicalIndex) => {
    const segmentIndex = segmentIndexByRowId.get(row.id)
    if (translationStatusByRowId.get(row.id) !== 'transposed' || typeof segmentIndex !== 'number') {
      return
    }
    const hasActualOrderInversion = rows.some((candidate, candidateIndex) => {
      if (candidate.id === row.id) {
        return false
      }
      const candidateSegmentIndex = segmentIndexByRowId.get(candidate.id)
      if (typeof candidateSegmentIndex !== 'number') {
        return false
      }
      if (candidateIndex < canonicalIndex && candidateSegmentIndex > segmentIndex) {
        return true
      }
      if (candidateIndex > canonicalIndex && candidateSegmentIndex < segmentIndex) {
        return true
      }
      return false
    })
    if (hasActualOrderInversion) {
      projectedSourceRowIds.add(row.id)
    }
  })

  const projectionRows: LedgerSafeProjectedDisplayRow[] = []
  rows.forEach(row => {
    if (!projectedSourceRowIds.has(row.id)) {
      return
    }
    const segmentIndex = segmentIndexByRowId.get(row.id) as number

    let insertAfterRowId: string | null = null
    let bestSegmentIndex = Number.NEGATIVE_INFINITY

    rows.forEach(candidate => {
      // Anchors must be rows displayed at their canonical position: anchoring one
      // projection after another projected row's canonical (ghosted) slot would
      // re-introduce the very inversion the projection exists to resolve.
      if (candidate.id === row.id || projectedSourceRowIds.has(candidate.id)) {
        return
      }
      const candidateSegmentIndex = segmentIndexByRowId.get(candidate.id)
      if (typeof candidateSegmentIndex !== 'number') {
        return
      }
      if (candidateSegmentIndex < segmentIndex && candidateSegmentIndex > bestSegmentIndex) {
        bestSegmentIndex = candidateSegmentIndex
        insertAfterRowId = candidate.id
      }
    })

    projectionRows.push({
      key: insertAfterRowId
        ? `projection:${row.id}:after:${insertAfterRowId}`
        : `projection:${row.id}:before-first`,
      row,
      kind: 'projection' as const,
      sourceRowId: row.id,
      insertAfterRowId,
      segmentIndex,
    })
  })

  projectionRows.sort((left, right) => {
    const leftSegment = left.segmentIndex
    const rightSegment = right.segmentIndex
    // mutation-ignore-next-line equivalent: final display order is governed by each projection's insertion anchor.
    if (leftSegment !== rightSegment) {
      return leftSegment - rightSegment
    }
    return (canonicalIndexByRowId.get(left.sourceRowId) ?? 0) - (canonicalIndexByRowId.get(right.sourceRowId) ?? 0)
  })

  const projectionsBeforeFirst = projectionRows.filter(row => row.insertAfterRowId === null)
  const projectionsByAnchor = new Map<string, LedgerSafeTranspositionDisplayRow[]>()
  projectionRows.forEach(row => {
    if (!row.insertAfterRowId) {
      return
    }
    const bucket = projectionsByAnchor.get(row.insertAfterRowId) || []
    bucket.push(row)
    projectionsByAnchor.set(row.insertAfterRowId, bucket)
  })

  const displayRows: LedgerSafeTranspositionDisplayRow[] = [
    ...projectionsBeforeFirst,
  ]
  rows.forEach(row => {
    displayRows.push({
      key: `canonical:${row.id}`,
      row,
      kind: 'canonical',
      sourceRowId: row.id,
      insertAfterRowId: null,
      segmentIndex: segmentIndexByRowId.get(row.id) ?? null,
    })
    const anchoredProjections = projectionsByAnchor.get(row.id)
    if (anchoredProjections?.length) {
      displayRows.push(...anchoredProjections)
    }
  })

  return {
    displayRows,
    projectedSourceRowIds,
  }
}
